// C11 — Full notifications page (route /notifications).
//
// Cursor-paginated list with category + unread-only + include-archived
// filters. Per-row archive button. Mark-all-read shortcut.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import NotificationItem from '../components/shared/NotificationItem';
import { notificationsApi } from '../lib/notificationsApi';

const PAGE_SIZE = 25;

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'collaboration', label: 'Collaboration' },
  { value: 'system', label: 'System' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'project', label: 'Project' },
  { value: 'payment', label: 'Payment' },
];

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [category, setCategory] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const fetchPage = useCallback(async ({ cursor = null } = {}) => {
    const isInitial = !cursor;
    if (isInitial) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    try {
      const data = await notificationsApi.list({
        cursor: cursor || undefined,
        limit: PAGE_SIZE,
        category: category || undefined,
        unread_only: unreadOnly || undefined,
        include_archived: includeArchived || undefined,
      });
      const rows = Array.isArray(data?.notifications) ? data.notifications : [];
      setItems((prev) => isInitial ? rows : [...prev, ...rows]);
      setNextCursor(data?.next_cursor || null);
      setHasMore(Boolean(data?.has_more));
    } catch (err) {
      setError(err.message || 'Could not load notifications.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category, unreadOnly, includeArchived]);

  // Refetch whenever filters change.
  useEffect(() => { fetchPage(); }, [fetchPage]);

  // ── Item interactions (optimistic) ───────────────────────────────────
  const handleOpen = useCallback(async (notif) => {
    if (!notif.read) {
      setItems((rows) => rows.map((r) => r.id === notif.id ? { ...r, read: true } : r));
      notificationsApi.markRead(notif.id).catch(() => {});
    }
    if (notif.action_url) navigate(notif.action_url);
  }, [navigate]);

  const handleArchive = useCallback(async (id) => {
    setItems((rows) =>
      includeArchived
        ? rows.map((r) => r.id === id ? { ...r, archived: true } : r)
        : rows.filter((r) => r.id !== id)
    );
    try { await notificationsApi.archive(id); }
    catch { fetchPage(); }
  }, [includeArchived, fetchPage]);

  const handleUnarchive = useCallback(async (id) => {
    setItems((rows) => rows.map((r) => r.id === id ? { ...r, archived: false } : r));
    try { await notificationsApi.unarchive(id); }
    catch { fetchPage(); }
  }, [fetchPage]);

  const handleMarkAllRead = useCallback(async () => {
    setItems((rows) => rows.map((r) => ({ ...r, read: true })));
    try { await notificationsApi.markAllRead({ category: category || undefined }); }
    catch { fetchPage(); }
  }, [category, fetchPage]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8" data-testid="notifications-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={loading || items.every((r) => r.read)}
            data-testid="page-mark-all-read-btn"
          >
            Mark all read
          </Button>
        </div>

        {/* Filter bar */}
        <div
          className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-white border border-gray-100 rounded-lg"
          data-testid="filter-bar"
        >
          <label className="text-xs font-medium text-gray-600">
            Category
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="ml-2 text-sm border border-gray-200 rounded-md px-2 py-1"
              data-testid="filter-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-700 ml-2">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              data-testid="filter-unread"
            />
            Unread only
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="filter-archived"
            />
            Show archived
          </label>
        </div>

        {/* List */}
        <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          )}
          {!loading && error && (
            <p className="px-4 py-6 text-sm text-red-600" data-testid="list-error">
              {error}
            </p>
          )}
          {!loading && !error && items.length === 0 && (
            <EmptyNotifications hasFilters={Boolean(category || unreadOnly || includeArchived)} />
          )}
          {!loading && !error && items.length > 0 && (
            <ul className="divide-y divide-gray-100" data-testid="page-notification-list">
              {items.map((notif) => (
                <NotificationItem
                  key={notif.id}
                  notif={notif}
                  onClickOpen={handleOpen}
                  onArchive={handleArchive}
                  onUnarchive={notif.archived ? handleUnarchive : null}
                />
              ))}
            </ul>
          )}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fetchPage({ cursor: nextCursor })}
              disabled={loadingMore}
              data-testid="load-more-btn"
            >
              {loadingMore && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Load more
            </Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function EmptyNotifications({ hasFilters }) {
  return (
    <div className="text-center py-14 px-6" data-testid="page-empty">
      <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
      <p className="text-sm text-gray-700 font-medium">
        {hasFilters ? 'No notifications match these filters' : "You're all caught up"}
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {hasFilters
          ? 'Try clearing the category or unread filter.'
          : "We'll notify you when something happens."}
      </p>
    </div>
  );
}
